import { MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useCallback, useEffect, useMemo } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { Navigate } from "react-router-dom"
import { toast } from "react-toastify"
import { digitalManagerApi } from "../../../../../../../services/api"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../../../components/DiMaDetailsContent"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToApplicationFeature,
} from "../../../../../../models/Authorization"
import {
  ApplicationPermissionFirmwareConstraintGet,
  ApplicationPermissionFirmwareConstraintPut,
} from "../../../../../../models/ApplicationPermissionFirmwareConstraint"
import { UserInfo } from "../../../../../../models/User"
import { createApplicationPermissionFirmwareConstraintValidationSchema } from "../../../../../../components/Helpers"

const validationSchema = createApplicationPermissionFirmwareConstraintValidationSchema()

export function ApplicationPermissionFirmwareConstraintDetails(props: {
  readonly firmwareConstraint: ApplicationPermissionFirmwareConstraintGet
  readonly applicationId: string
  readonly permissionId: string
  readonly user: UserInfo
  readonly setFirmwareConstraint: (
    updated: ApplicationPermissionFirmwareConstraintGet
  ) => void
}) {
  const { firmwareConstraint, applicationId, permissionId, user, setFirmwareConstraint } =
    props

  const returnURL = `/applications/${applicationId}/permissions/${permissionId}#fw-constraints`

  const formMethods = useForm<ApplicationPermissionFirmwareConstraintPut>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = formMethods

  useEffect(() => {
    reset({
      mainboardMinSoftwareVersion: firmwareConstraint.mainboardMinSoftwareVersion,
      hmiMinSoftwareVersion: firmwareConstraint.hmiMinSoftwareVersion,
    })
  }, [firmwareConstraint, reset])

  const onSubmit = useCallback(
    (values: ApplicationPermissionFirmwareConstraintPut) =>
      digitalManagerApi
        .put<ApplicationPermissionFirmwareConstraintGet>(
          `/applications/${applicationId}/permissions/${permissionId}/firmwareConstraints/${firmwareConstraint.id}`,
          values
        )
        .then((res) => {
          toast.success("Firmware Constraint updated")
          setFirmwareConstraint(res.data)
        })
        .catch(() => {
          toast.error("Cannot update Firmware Constraint")
          throw new Error("Cannot update Firmware Constraint")
        }),
    [applicationId, permissionId, firmwareConstraint.id, setFirmwareConstraint]
  )

  const canWrite = useMemo(
    () =>
      hasUserAccessToApplicationFeature(
        user.authorizations.applications,
        applicationId,
        DimaFeatureShortNameEnum.AM,
        AccessLevel.Write
      ),
    [user.authorizations.applications, applicationId]
  )

  const handleTextFieldChange = useCallback(
    (
      field: ControllerRenderProps<
        ApplicationPermissionFirmwareConstraintPut,
        "mainboardMinSoftwareVersion" | "hmiMinSoftwareVersion"
      >
    ) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const renderMainboardField = useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<
        ApplicationPermissionFirmwareConstraintPut,
        "mainboardMinSoftwareVersion"
      >
    }) => (
      <MUITextField
        id="mainboardMinSoftwareVersion"
        type="text"
        size={32}
        name={field.name}
        value={field.value ?? ""}
        placeholder="XX.XX.XX"
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={!canWrite}
        errors={
          typeof errors.mainboardMinSoftwareVersion?.message === "string"
            ? {
                mainboardMinSoftwareVersion: {
                  message: errors.mainboardMinSoftwareVersion.message,
                },
              }
            : undefined
        }
      />
    ),
    [canWrite, errors.mainboardMinSoftwareVersion, handleTextFieldChange]
  )

  const renderHmiField = useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<
        ApplicationPermissionFirmwareConstraintPut,
        "hmiMinSoftwareVersion"
      >
    }) => (
      <MUITextField
        id="hmiMinSoftwareVersion"
        type="text"
        size={32}
        name={field.name}
        value={field.value ?? ""}
        placeholder="XX.XX.XX"
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={!canWrite}
        errors={
          typeof errors.hmiMinSoftwareVersion?.message === "string"
            ? {
                hmiMinSoftwareVersion: {
                  message: errors.hmiMinSoftwareVersion.message,
                },
              }
            : undefined
        }
      />
    ),
    [canWrite, errors.hmiMinSoftwareVersion, handleTextFieldChange]
  )

  if (!firmwareConstraint) {
    return <Navigate to={returnURL} />
  }

  const detailsContentProps: DiMaDetailsContentProps<ApplicationPermissionFirmwareConstraintPut> =
    {
      formMethods,
      canWrite,
      inlineEdit: true,
      listItems: [
        {
          id: 0,
          label: "Device",
          displayItem: (
            <div style={{ width: "100%" }}>
              {`${firmwareConstraint.device?.slaveId} - ${firmwareConstraint.device?.name}`}
            </div>
          ),
        },
        {
          id: 1,
          label: "Mainboard Min SW Version",
          displayItem: (
            <div style={{ width: "100%" }}>
              {firmwareConstraint.mainboardMinSoftwareVersion}
            </div>
          ),
          editItem: (
            <Controller
              name="mainboardMinSoftwareVersion"
              control={control}
              render={renderMainboardField}
            />
          ),
        },
        {
          id: 2,
          label: "HMI Min SW Version",
          displayItem: (
            <div style={{ width: "100%" }}>{firmwareConstraint.hmiMinSoftwareVersion}</div>
          ),
          editItem: (
            <Controller
              name="hmiMinSoftwareVersion"
              control={control}
              render={renderHmiField}
            />
          ),
        },
      ],
    }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
